/**
 * View-mode formatting for EV/BV financial values from payer / Postgres payloads.
 *
 * @module coverageInfo/helpers/evCoverageFinancialDisplay
 */

/** Discriminator for currency vs percentage display. */
export type BvFinancialValueKind = "money" | "percent";

const MISSING_DISPLAY = "-";
const NA_DISPLAY = "NA";

/**
 * Formats a single financial value for read-only EV/BV display.
 *
 * @param value - Raw backend string, parsed number, or absent value
 * @param kind - Currency (`$`) or percentage (`%`) formatting for numeric values
 * @returns `NA`, `-`, formatted number, or the original literal when non-numeric
 */
export const formatBvFinancialDisplay = (
  value: string | number | undefined | null,
  kind: BvFinancialValueKind,
): string => {
  if (value === undefined || value === null) {
    return MISSING_DISPLAY;
  }

  const trimmed = String(value).trim();
  if (trimmed === "") {
    return MISSING_DISPLAY;
  }
  if (trimmed === "-" || trimmed === "—") {
    return MISSING_DISPLAY;
  }

  const upper = trimmed.toUpperCase();
  if (upper === "NA" || upper === "N/A") {
    return NA_DISPLAY;
  }

  const numericPart = trimmed.replace(/[$%,]/g, "").replace(/,/g, "").trim();
  const parsed = Number.parseFloat(numericPart);
  if (Number.isFinite(parsed)) {
    if (kind === "money") {
      return `$${parsed.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    const pctText = Number.isInteger(parsed)
      ? String(parsed)
      : parsed.toFixed(2);
    return `${pctText}%`;
  }

  return trimmed;
};

/**
 * Formats a deductible/OOP field using the raw backend string when present.
 *
 * @param raw - Unparsed value from the payer payload
 * @param parsed - Numeric value used for validation and cross-field rules
 * @param kind - Currency or percentage display
 * @returns Formatted label for `LabeledValue` read mode
 */
export const formatBvAmountFieldDisplay = (
  raw: string | undefined,
  parsed: number | undefined,
  kind: BvFinancialValueKind = "money",
): string => {
  const rawTrimmed = raw?.trim() ?? "";
  if (rawTrimmed !== "") {
    return formatBvFinancialDisplay(raw, kind);
  }
  if (parsed !== undefined && !Number.isNaN(parsed)) {
    return formatBvFinancialDisplay(parsed, kind);
  }
  return formatBvFinancialDisplay(null, kind);
};

/**
 * Normalizes a payer amount for Postgres write-back.
 *
 * @param raw - Raw text held in the UI model
 * @param parsed - Parsed number when the field is numeric
 * @returns `NA`, `-`, numeric string, or undefined when unset
 */
export const bvFinancialValueToDbString = (
  raw: string | undefined,
  parsed: number | undefined,
): string | undefined => {
  const trimmed = (raw ?? "").trim();
  if (trimmed !== "") {
    const upper = trimmed.toUpperCase();
    if (upper === "NA" || upper === "N/A") {
      return "NA";
    }
    if (trimmed === "-" || trimmed === "—") {
      return "-";
    }
    const numericPart = trimmed.replace(/[$%,]/g, "").replace(/,/g, "").trim();
    const n = Number.parseFloat(numericPart);
    if (Number.isFinite(n)) {
      return String(n);
    }
    return trimmed;
  }
  if (parsed !== undefined && !Number.isNaN(parsed)) {
    return String(parsed);
  }
  return undefined;
};
